import * as THREE from 'three';

const _handWorldScale = new THREE.Vector3();
const _gripLocal = new THREE.Matrix4();
const _attachM = new THREE.Matrix4();
const _weaponM = new THREE.Matrix4();
const _attachQ = new THREE.Quaternion();
const _attachP = new THREE.Vector3();
const _one = new THREE.Vector3(1, 1, 1);
const _decP = new THREE.Vector3();
const _decQ = new THREE.Quaternion();
const _decS = new THREE.Vector3();

const _claspTarget = new THREE.Vector3();
const _claspEe = new THREE.Vector3();
const _claspOrigin = new THREE.Vector3();
const _claspToEe = new THREE.Vector3();
const _claspToTg = new THREE.Vector3();
const _claspAxis = new THREE.Vector3();
const _claspQ = new THREE.Quaternion();
const _claspWorldQ = new THREE.Quaternion();
const _claspParentQ = new THREE.Quaternion();
const _claspLocal = new THREE.Vector3();

export type FpsGripAttach = { x: number; y: number; z: number; rx: number; ry: number; rz: number };

/**
 * Seat the viewmodel gun in the FPS hand using grip_r + a hip/ADS attach pose.
 * Parent is fpsHandR (same +Y back / −Z fingers frame as the first-person hold).
 */
export function attachWeaponByFpsGrip(
  fpsHand: THREE.Object3D,
  weapon: THREE.Object3D,
  attach: FpsGripAttach,
): void {
  let hold = fpsHand.getObjectByName('tp_weapon_hold');
  if (!hold) {
    hold = new THREE.Group();
    hold.name = 'tp_weapon_hold';
    fpsHand.add(hold);
  }
  fpsHand.updateWorldMatrix(true, false);
  fpsHand.getWorldScale(_handWorldScale);
  hold.scale.setScalar(1 / Math.max(Math.abs(_handWorldScale.x), 1e-6));

  if (weapon.parent !== hold) hold.add(weapon);
  weapon.name = 'tp_weapon';
  seatWeaponByFpsGrip(weapon, attach);
}

/** Update an already-parented TP weapon's local seat (hip ↔ ADS lerp). */
export function seatWeaponByFpsGrip(weapon: THREE.Object3D, attach: FpsGripAttach): void {
  const grip = weapon.getObjectByName('grip_r');
  weapon.position.set(0, 0, 0);
  weapon.quaternion.identity();
  weapon.scale.setScalar(1);
  if (!grip) return;

  weapon.updateMatrixWorld(true);
  grip.updateMatrixWorld(true);
  _gripLocal.copy(weapon.matrixWorld).invert().multiply(grip.matrixWorld);
  _attachP.set(attach.x, attach.y, attach.z);
  _attachQ.setFromEuler(new THREE.Euler(attach.rx, attach.ry, attach.rz, 'YXZ'));
  _attachM.compose(_attachP, _attachQ, _one).invert();
  _weaponM.copy(_gripLocal).invert();
  _weaponM.premultiply(_attachM);
  _weaponM.decompose(_decP, _decQ, _decS);
  weapon.position.copy(_decP);
  weapon.quaternion.copy(_decQ);
  weapon.rotation.setFromQuaternion(_decQ, 'YXZ');
  weapon.scale.setScalar(1);
}

export function lerpFpsGripAttach(a: FpsGripAttach, b: FpsGripAttach, t: number): FpsGripAttach {
  const u = THREE.MathUtils.clamp(t, 0, 1);
  return {
    x: a.x + (b.x - a.x) * u,
    y: a.y + (b.y - a.y) * u,
    z: a.z + (b.z - a.z) * u,
    rx: a.rx + (b.rx - a.rx) * u,
    ry: a.ry + (b.ry - a.ry) * u,
    rz: a.rz + (b.rz - a.rz) * u,
  };
}

/**
 * CCD the support arm so the left hand sits on grip_l (or a forend fallback).
 */
export function claspSupportHand(
  leftArm: THREE.Object3D,
  leftForeArm: THREE.Object3D,
  leftHand: THREE.Object3D,
  rightHand: THREE.Object3D,
  weapon: THREE.Object3D,
  forend = 0,
): void {
  rightHand.updateWorldMatrix(true, true);
  weapon.updateWorldMatrix(true, true);
  const gripL = weapon.getObjectByName('grip_l');
  if (gripL) {
    gripL.getWorldPosition(_claspTarget);
  } else if (forend > 0) {
    weapon.localToWorld(_claspTarget.set(0, 0, -forend));
  } else {
    rightHand.localToWorld(_claspLocal.set(0.05, 0.01, 0.04));
    rightHand.getWorldPosition(_claspTarget);
    _claspTarget.lerp(_claspLocal, 0.85);
  }

  reachArmTo(leftArm, leftForeArm, leftHand, _claspTarget, 3);
}

/** Bend an arm chain so `hand` reaches `targetWorld`. */
export function reachArmTo(
  arm: THREE.Object3D,
  foreArm: THREE.Object3D,
  hand: THREE.Object3D,
  targetWorld: THREE.Vector3,
  iterations = 4,
): void {
  for (let i = 0; i < iterations; i++) {
    ccdRotateBoneToward(foreArm, hand, targetWorld, 0.42);
    ccdRotateBoneToward(arm, hand, targetWorld, 0.28);
  }
}

function ccdRotateBoneToward(
  bone: THREE.Object3D,
  endEffector: THREE.Object3D,
  targetWorld: THREE.Vector3,
  weight: number,
): void {
  bone.updateWorldMatrix(true, false);
  endEffector.updateWorldMatrix(true, false);
  bone.getWorldPosition(_claspOrigin);
  endEffector.getWorldPosition(_claspEe);
  _claspToEe.subVectors(_claspEe, _claspOrigin);
  _claspToTg.subVectors(targetWorld, _claspOrigin);
  if (_claspToEe.lengthSq() < 1e-8 || _claspToTg.lengthSq() < 1e-8) return;
  _claspToEe.normalize();
  _claspToTg.normalize();
  _claspAxis.crossVectors(_claspToEe, _claspToTg);
  if (_claspAxis.lengthSq() < 1e-10) return;
  _claspAxis.normalize();
  const angle = Math.acos(THREE.MathUtils.clamp(_claspToEe.dot(_claspToTg), -1, 1)) * weight;
  if (Math.abs(angle) < 1e-4) return;
  _claspQ.setFromAxisAngle(_claspAxis, angle);
  bone.getWorldQuaternion(_claspWorldQ);
  _claspWorldQ.premultiply(_claspQ);
  const parent = bone.parent;
  if (parent) {
    parent.getWorldQuaternion(_claspParentQ);
    bone.quaternion.copy(_claspParentQ.invert().multiply(_claspWorldQ));
  } else {
    bone.quaternion.copy(_claspWorldQ);
  }
  bone.updateMatrix();
}
